import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BellRing, BellOff, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { enableWebPush, pushPromptMode, dismissPushPrompt, dismissBlockedHint, permissionState } from "@/lib/push";

const REVEAL_DELAY = 800; // brief settle after app open; the popup then stays until the user acts

// Non-blocking popup shown on app open:
//  - "enable"      → prompt with the Enable button (reuses enableWebPush, same as Settings)
//  - "blocked-ios" → an "enable in iOS Settings → RoyalChat Nova" hint (permission denied on iOS)
export default function NotificationPrompt() {
  const { lang, t } = useI18n();
  const fr = lang === "fr";
  const [mode, setMode] = useState(null); // null | "enable" | "blocked-ios"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      const m = await pushPromptMode();
      if (!cancelled && m) setMode(m);
    }, REVEAL_DELAY);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const notNow = () => { dismissPushPrompt(); setMode(null); };
  const closeBlocked = () => { dismissBlockedHint(); setMode(null); };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await enableWebPush();
      if (r.ok) toast.success(t("notifications_on") || "Notifications enabled");
      else if (r.reason === "denied") toast.error(t("notifications_blocked") || (fr ? "Notifications bloquées." : "Notifications blocked."), { duration: 8000 });
      else if (r.reason === "ios-not-installed") toast(t("ios_install_for_push") || "On iPhone: tap Share → Add to Home Screen, then open RoyalChat Nova from your Home Screen.", { duration: 8000 });
    } catch (e) {
      toast.error(t("set_notif_fail") || (fr ? "Échec" : "Failed"));
    } finally {
      setBusy(false);
      if (permissionState() === "default") dismissPushPrompt();
      setMode(null);
    }
  };

  return (
    <AnimatePresence>
      {mode && (
        <motion.div
          initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          data-testid={mode === "blocked-ios" ? "notif-blocked-hint" : "notif-prompt"}
          className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-[70] mx-auto max-w-sm">
          <div className="relative overflow-hidden rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-surface)]/95 backdrop-blur-xl shadow-[0_18px_50px_-12px_rgba(212,175,55,0.4)]">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 12% 0%, rgba(212,175,55,0.12), transparent 55%)" }} />

            {mode === "blocked-ios" ? (
              <>
                <div className="relative flex items-center gap-3 p-3.5">
                  <span className="shrink-0 h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)]/12 border border-[var(--rc-gold)]/25 text-[var(--rc-gold)]">
                    <BellOff size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[var(--rc-text)] leading-tight">
                      {fr ? "Notifications bloquées" : "Notifications are blocked"}
                    </p>
                    <p className="text-xs text-[var(--rc-muted)] leading-snug mt-0.5">
                      {fr ? "Activez-les dans Réglages iOS → RoyalChat Nova → Notifications." : "Turn them on in iOS Settings → RoyalChat Nova → Notifications."}
                    </p>
                  </div>
                  <button onClick={closeBlocked} data-testid="notif-blocked-close"
                    className="shrink-0 h-7 w-7 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-bg)] transition-colors" aria-label="Dismiss"><X size={16} /></button>
                </div>
                <div className="relative flex border-t border-[var(--rc-gold)]/20">
                  <button onClick={closeBlocked} data-testid="notif-blocked-gotit"
                    className="flex-1 bg-[var(--rc-gold)]/10 py-2.5 text-sm font-semibold text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/15 transition-colors">
                    {fr ? "Compris" : "Got it"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="relative flex items-center gap-3 p-3.5">
                  <motion.span
                    animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    className="shrink-0 h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)]/12 border border-[var(--rc-gold)]/25 text-[var(--rc-gold)]">
                    <BellRing size={20} />
                  </motion.span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[var(--rc-text)] leading-tight">
                      {fr ? "Activez les notifications" : "Turn on notifications"}
                    </p>
                    <p className="text-xs text-[var(--rc-muted)] leading-snug mt-0.5">
                      {fr ? "Soyez alerté des nouveaux messages et appels, même quand l'app est fermée." : "Get alerted to new messages and calls, even when the app is closed."}
                    </p>
                  </div>
                  <button onClick={notNow} data-testid="notif-prompt-dismiss"
                    className="shrink-0 h-7 w-7 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-bg)] transition-colors" aria-label="Dismiss"><X size={16} /></button>
                </div>
                <div className="relative flex border-t border-[var(--rc-gold)]/20">
                  <button onClick={notNow} data-testid="notif-prompt-notnow"
                    className="flex-1 py-2.5 text-sm font-medium text-[var(--rc-muted)] hover:bg-[var(--rc-bg)] transition-colors">
                    {fr ? "Plus tard" : "Not now"}
                  </button>
                  <button onClick={enable} disabled={busy} data-testid="notif-prompt-enable"
                    className="flex-1 border-l border-[var(--rc-gold)]/20 bg-[var(--rc-gold)]/10 py-2.5 text-sm font-semibold text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/15 transition-colors disabled:opacity-50">
                    {busy ? "…" : (fr ? "Activer" : "Enable Notifications")}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
